/**
 * Рубли из копеек с точностью до копейки: «12 345,67 ₽».
 *
 * Не короткая форма витрины (formatMoneyShort в catalogFormat): там сравнивают
 * склады и «2,5 млн ₽» достаточно, здесь баланс — сумма, с которой сверяют
 * платёжку, и округлять её нельзя ни на копейку.
 *
 * Всё считается в BigInt: суммы приходят копейками в строке (GraphQL Int
 * 32-битный), и Number на них однажды молча потеряет точность.
 */
export function formatKopecks(kopecks: string | null | undefined): string {
	if (kopecks === null || kopecks === undefined || kopecks === '') {
		return '—';
	}

	const value = BigInt(kopecks);
	const negative = value < 0n;
	const abs = negative ? -value : value;

	/*
	 * Разряды разделяются обычным пробелом, а не toLocaleString.
	 *
	 * Тот вставляет неразрывный (U+00A0), и одна и та же сумма на двух экранах
	 * одного раздела выглядела одинаково, но была разной строкой: сверка глазами
	 * проходила, а поиском по странице и автотестом — нет. Пробел здесь тот же,
	 * что во всех остальных местах, где мы печатаем деньги.
	 */
	const rubles = (abs / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
	const rest = (abs % 100n).toString().padStart(2, '0');

	// Типографский минус, а не дефис: в сумме он читается знаком, а не чёрточкой
	return `${negative ? '−' : ''}${rubles},${rest} ₽`;
}

/**
 * Копейки из строки поля «сумма в рублях»: «12 345,67» → 1234567n.
 *
 * null — ввод не разобрать (или ноль): вызывающий по нему блокирует кнопку,
 * а не отправляет на сервер заведомый отказ. Разделитель копеек — запятая
 * или точка: суммы копируют из бухгалтерских программ, а там встречается и то,
 * и другое. Всё считается в BigInt по той же причине, что в formatKopecks.
 */
export function parseRublesToKopecks(input: string): bigint | null {
	// Пробелы — разделители разрядов; \s покрывает и неразрывные (U+00A0, U+202F)
	// из скопированных сумм: в JS он матчит всю категорию Space_Separator
	const cleaned = input.replace(/\s/g, '').replace(',', '.');
	const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);

	if (!match) {
		return null;
	}

	const kopecks = BigInt(match[1]) * 100n + BigInt((match[2] ?? '').padEnd(2, '0') || '0');

	return kopecks > 0n ? kopecks : null;
}

/**
 * Сумма «около»: «~1 200 ₽» до выполнения, а без суммы — просто «—». Тильда
 * у прочерка («~—») читалась бы как «примерно ничего»: службе безопасности
 * стоимость услуг склада не отдаётся вовсе (SKS-819).
 */
export function approxKopecks(kopecks: string | null | undefined): string {
	const formatted = formatKopecks(kopecks);

	return formatted === '—' ? formatted : `~${formatted}`;
}
